import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Star, Users, Clock, Sparkles, DollarSign, Briefcase } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Bundle, Course } from '@/types/api.types';
import { bundleService } from '@/services/bundle.service';
import { courseService } from '@/services/course.service';
import BundleModal from '@/components/modals/BundleModal';
import CourseModal from '@/components/modals/CourseModal';
import CourseBundlesSection from '@/components/sections/CourseBundlesSection';
import IndividualCoursesSection from '@/components/sections/IndividualCoursesSection';
import { startEnrollment } from '@/utils/enrollment';
import { useNavigate } from 'react-router-dom';
import LearningTracksSection from '@/components/sections/LearningTracksSection';
import { trackCourseCard, trackBundleCard, trackModal, trackPageView, trackCTA } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { useSaleInfo } from '@/hooks/useSaleInfo';
import { metaPixel } from '@/services/metaPixel';

// Enterprise-grade interface for bundle courses mapping
interface BundleCoursesMap {
  [bundleId: string]: {
    courses: Course[];
    loading: boolean;
    error: string | null;
    allHidden: boolean;
  };
}

export default function CoursesPage() {
  const [expandedBundle, setExpandedBundle] = useState<string | null>(null);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [bundleCourses, setBundleCourses] = useState<BundleCoursesMap>({});
  const [bundleLoading, setBundleLoading] = useState(true);
  const [courseLoading, setCourseLoading] = useState(true);
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [isBundleModalOpen, setIsBundleModalOpen] = useState(false);
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);

  const navigate = useNavigate();

  // Get sale info for displaying sale badges
  const { isSaleActive } = useSaleInfo();

  // Track scroll depth and page view
  useScrollTracking('Courses');

  useEffect(() => {
    // Meta Pixel
    metaPixel.init();
    metaPixel.pageView();
    trackPageView('Courses');
  }, []);

  useEffect(() => {
    fetchBundles();
    fetchCourses();
  }, []);

  /**
   * Auto-fetch courses for all bundles when bundles are loaded
   * This ensures the Individual Courses section has all courses available
   */
  useEffect(() => {
    if (bundles.length > 0 && !bundleLoading) {
      bundles.forEach((bundle) => {
        const bundleCourseData = bundleCourses[bundle.id];
        // Only fetch if not already loaded or loading
        if (bundleCourseData && bundleCourseData.courses.length === 0 && !bundleCourseData.loading && !bundleCourseData.error) {
          fetchCoursesForBundle(bundle.id, Array.isArray(bundle.course_ids) ? bundle.course_ids : []);
        }
      });
    }
  }, [bundles, bundleLoading]);

  /**
   * Fetch all bundles with error handling
   */
  const fetchBundles = async () => {
    try {
      setBundleLoading(true);
      const response = await bundleService.getAllBundles({ is_active: true, hidden: false });
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch bundles');
      }
      
      const fetchedBundles = response.data.bundles || [];
      setBundles(fetchedBundles);
      
      // Initialize bundle courses map
      const initialMap: BundleCoursesMap = {};
      fetchedBundles.forEach((bundle) => {
        initialMap[bundle.id] = {
          courses: [],
          loading: false,
          error: null,
          allHidden: false,
        };
      });
      setBundleCourses(initialMap);
    } catch (error) {
      console.error('Error fetching bundles:', error);
    } finally {
      setBundleLoading(false);
    }
  };

  /**
   * Fetch all courses with error handling
   */
  const fetchCourses = async () => {
    try {
      setCourseLoading(true);
      const response = await courseService.getAllCourses({ is_active: true, hidden: false });
      if (response.success && response.data) {
        setCourses(response.data.courses || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setCourseLoading(false);
    }
  };

  /**
   * Fetch courses for a specific bundle with individual API calls
   * This ensures we get ALL courses associated with the bundle
   */
  const fetchCoursesForBundle = async (bundleId: string, courseIds: string[]) => {
    // Ensure courseIds is a valid array
    const validCourseIds = Array.isArray(courseIds) ? courseIds : [];

    if (validCourseIds.length === 0) {
      setBundleCourses(prev => ({
        ...prev,
        [bundleId]: { courses: [], loading: false, error: 'No courses in this bundle', allHidden: false }
      }));
      return;
    }

    // Set loading state
    setBundleCourses(prev => ({
      ...prev,
      [bundleId]: { ...prev[bundleId], loading: true, error: null }
    }));

    try {
      // Fetch all courses in parallel for this bundle
      const coursePromises = validCourseIds.map(courseId =>
        courseService.getCourseById(courseId)
          .catch(err => {
            console.error(`Failed to fetch course ${courseId}:`, err);
            return null;
          })
      );

      const courseResponses = await Promise.all(coursePromises);

      // Filter successful responses and extract course data
      const allFetchedCourses = courseResponses
        .filter(response => response && response.success && response.data)
        .map(response => response!.data as Course);

      const visibleCourses = allFetchedCourses.filter(course => !course.hidden);

      // Check if courses were fetched but all are hidden
      const allHidden = allFetchedCourses.length > 0 && visibleCourses.length === 0;

      // Update state with fetched courses
      setBundleCourses(prev => ({
        ...prev,
        [bundleId]: {
          courses: visibleCourses,
          loading: false,
          error: visibleCourses.length === 0 && !allHidden ? 'Failed to load courses' : null,
          allHidden
        }
      }));
    } catch (error) {
      console.error(`Error fetching courses for bundle ${bundleId}:`, error);
      setBundleCourses(prev => ({
        ...prev,
        [bundleId]: {
          courses: [],
          loading: false,
          error: 'Failed to load courses. Please try again.',
          allHidden: false
        }
      }));
    }
  };

  /**
   * Toggle bundle expansion and fetch courses if not already loaded
   */
  const toggleBundle = (bundleId: string, bundle: Bundle) => {
    const isExpanding = expandedBundle !== bundleId;
    setExpandedBundle(isExpanding ? bundleId : null);

    // Fetch courses if expanding and not already loaded
    if (isExpanding && bundleCourses[bundleId]?.courses.length === 0 && !bundleCourses[bundleId]?.loading) {
      fetchCoursesForBundle(bundleId, Array.isArray(bundle.course_ids) ? bundle.course_ids : []);
    }
  };

  /**
   * Retry fetching courses for a bundle
   */
  const retryFetchCourses = (bundleId: string, courseIds: string[]) => {
    fetchCoursesForBundle(bundleId, courseIds);
  };

  const handleBundleClick = (bundleId: string) => {
    setSelectedBundleId(bundleId);
    setIsBundleModalOpen(true);
  };

  const handleCourseClick = (courseId: string) => {
    setSelectedCourseId(courseId);
    setIsCourseModalOpen(true);
  };

  const handleCloseBundleModal = () => {
    setIsBundleModalOpen(false);
    setSelectedBundleId(null);
  };

  const handleCloseCourseModal = () => {
    setIsCourseModalOpen(false);
    setSelectedCourseId(null);
  };

  const handleEnrollBundle = (bundleId: string) => {
    // TODO: Implement enrollment logic
    startEnrollment(bundleId, 'bundle', navigate)

    handleCloseBundleModal();
  };

  const handleEnrollCourse = (courseId: string) => {
    // TODO: Implement enrollment logic
    startEnrollment(courseId, 'course', navigate)

    handleCloseCourseModal();
  };

  const getBundleGradient = (title: string) => {
    if (title.toLowerCase().includes('core') || title.toLowerCase().includes('skill')) {
      return "from-purple-600 to-blue-600";
    } else if (title.toLowerCase().includes('entrepreneur') || title.toLowerCase().includes('business')) {
      return "from-blue-600 to-cyan-600";
    } else if (title.toLowerCase().includes('money') || title.toLowerCase().includes('income')) {
      return "from-green-600 to-emerald-600";
    }
    return "from-purple-600 to-blue-600";
  };

  const getBundleIcon = (title: string) => {
    if (title.toLowerCase().includes('core') || title.toLowerCase().includes('skill')) {
      return Sparkles;
    } else if (title.toLowerCase().includes('entrepreneur') || title.toLowerCase().includes('business')) {
      return Briefcase;
    } else if (title.toLowerCase().includes('money') || title.toLowerCase().includes('income')) {
      return DollarSign;
    }
    return Sparkles;
  };

  return (
    <>
      <Helmet>
        <title>AI Courses & Bundles - Machine Learning| AI Mastery Hub</title>
        <meta name="description" content="Browse our comprehensive AI courses and course bundles. Learn machine learning, deep learning, prompt engineering. Save up to 67% with bundles." />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-mastery-hub.com/courses" />
        <meta property="og:title" content="AI Courses & Bundles - Save Up To 67% | AI Mastery Hub" />
        <meta property="og:description" content="Browse comprehensive AI and tech courses. Learn machine learning. Bundle & save up to 67%." />
        <meta property="og:image" content="https://ai-mastery-hub.com/courses-og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://ai-mastery-hub.com/courses" />
        <meta property="twitter:title" content="AI Courses & Bundles - Save Up To 67%" />
        <meta property="twitter:description" content="Browse comprehensive AI and tech courses. Bundle & save up to 67%." />
        <meta property="twitter:image" content="https://ai-mastery-hub.com/courses-og-image.jpg" />

        {/* Additional SEO */}
        <meta name="keywords" content="AI courses, machine learning courses, deep learning training, prompt engineering courses, course bundles, online education" />
        <link rel="canonical" href="https://ai-mastery-hub.com/courses" />

        {/* Schema.org for Course Catalog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "AI Mastery Hub Courses",
            "description": "Comprehensive AI courses",
            "url": "https://ai-mastery-hub.com/courses"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        {/* Navigation */}
        <Navigation/>

        {/* Hero Section */}
        <section className="relative pt-32 pb-12 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
          }}></div>
          
          <div className="max-w-7xl mx-auto relative z-10 text-center">
            <div className="inline-block px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
              <span className="text-purple-400">🎓 Buy Individual Courses or Bundle & Save Up To 67%</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              <span className="block">Transform Your Future</span>
              <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                With AI Mastery
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Choose from our carefully crafted course bundles designed to take you from beginner to AI expert. 
              Build real skills, launch businesses, and create income streams with AI.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-purple-400" />
                <span className="text-gray-400">Growing Community</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-gray-400">Robust Curriculum</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-blue-400" />
                <span className="text-gray-400">Lifetime Access</span>
              </div>
            </div>
          </div>
        </section>

        {/* Learning Tracks Section */}
        <LearningTracksSection />

        {/* Course Bundles Section */}
        {/* <CourseBundlesSection
          bundleLoading={bundleLoading}
          bundles={bundles}
          bundleCourses={bundleCourses}
          expandedBundle={expandedBundle}
          onBundleClick={handleBundleClick}
          onToggleBundle={toggleBundle}
          onCourseClick={handleCourseClick}
          onRetryFetchCourses={retryFetchCourses}
          showSaleBadge={isSaleActive}
          isSaleActive={isSaleActive}
        /> */}

        {/* Individual Courses Section - Organized by Bundle */}
        {/* <IndividualCoursesSection
          bundleLoading={bundleLoading}
          bundles={bundles}
          bundleCourses={bundleCourses}
          onCourseClick={handleCourseClick}
          onRetryFetchCourses={retryFetchCourses}
          getBundleIcon={getBundleIcon}
          getBundleGradient={getBundleGradient}
          showSaleBadge={isSaleActive}
        /> */}

        {/* CTA Section */}
        <section className="py-20 px-6 bg-black">
          <div className="max-w-5xl mx-auto">
            <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-12 text-center overflow-hidden">
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Ready to Master AI?
                </h2>
                <p className="text-xl mb-8 text-white/90">
                  Join learners who are already transforming their careers with AI
                </p>
                <button 
                  onClick={() => { trackCTA.click('courses-get-started', 'Get Started Today', 'cta-section', '/sign-up'); navigate('/sign-up'); }}
                  className="bg-white text-black px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 hover:shadow-2xl"
                >
                  Get Started Today
                </button>
                <p className="text-sm mt-4 text-white/70">Get ready to get ahead.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>

      {/* Modals */}
      <BundleModal
        bundleId={selectedBundleId}
        isOpen={isBundleModalOpen}
        onClose={handleCloseBundleModal}
        onEnroll={handleEnrollBundle}
      />
      
      <CourseModal
        courseId={selectedCourseId}
        isOpen={isCourseModalOpen}
        onClose={handleCloseCourseModal}
        onEnroll={handleEnrollCourse}
      />
    </>
  );
}